
import {
  Box,
  Flex,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { clsx } from "clsx";
import { isEmpty } from "lodash";
import { useCallback, useContext, useRef } from "react";
import {
  MdsDoneRound
} from "react-icons-with-materialsymbols/mds";

import { Icon } from "@/design/components/icon";
import {
  isBool,
  normalizeValue,
} from "@/features/data-transformation/utils/helpers.ts";

import { FilterDetailsContext } from "../filter-details-context.ts";

import { EmptyMessage } from "./empty-message.tsx";
import { FilterByValueHeader } from "./header.tsx";
import { ValueSearch } from "./value-search.tsx";

export const FilterByValue = () => {
  const {
    selectedValues,
    onSelectValue,
    paginationDetails,
    filterItems,
    initialData,
  } = useContext(FilterDetailsContext)!;

  const {
    isLoading,
    isPaginating,
    hasMore,
    fetchNextPage,
  } = paginationDetails;

  
  const parentRef = useRef<HTMLDivElement>(null);
  
  const emptyCheck = (value: any) => (isEmpty(value) ? "(Blanks)" : value);

  const loadingContainer = isLoading && !isPaginating;

  const rowVirtualizer = useVirtualizer({
    count: (filterItems?.length ?? 0) + (hasMore ? 1 : 0),
    getScrollElement: () => parentRef.current,
    estimateSize: () => 100,
    overscan: 20,
    measureElement: (element) => element?.getBoundingClientRect().height || 100,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const handleLoadMore = useCallback(() => {
    if (fetchNextPage && !isLoading && !isPaginating) {
      fetchNextPage();
    }
  }, [fetchNextPage, isLoading, isPaginating]);

  return (
    <Flex className="flex-col w-full">
      <FilterByValueHeader />
      <ValueSearch />
      <Flex
        className={clsx(
          "flex-col w-full h-[180px] overflow-auto",
          loadingContainer && "justify-center items-center"
        )}
        ref={parentRef}
      >
        {loadingContainer && (
          <Spinner className="self-center justify-self-center" />
        )}

        {filterItems && filterItems.length > 0 && (
          <Box
            style={{
              height: `${totalSize}px`,
              width: "100%",
              position: "relative",
            }}
          >
            <Box
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
              }}
            >
              {virtualItems.map((virtualItem) => {
                const itemIndex = virtualItem.index;

                // If it's the last item and hasMore is true, render Load More button
                if (hasMore && itemIndex === filterItems.length) {
                  return (
                    <Flex
                      className="items-center border-b border-b-gray-100 p-2.5 cursor-pointer"
                      key={`${virtualItem.key}-loadmore`}
                      ref={rowVirtualizer.measureElement}
                      sx={{
                        // Prevent clicking when already loading
                        pointerEvents: isPaginating ? "none" : "auto",
                        opacity: isPaginating ? 0.7 : 1,
                      }}
                      data-index={virtualItem.index}
                      onClick={handleLoadMore}
                    >
                      {isPaginating ? (
                        <Spinner className="mr-2" size="sm" />
                      ) : null}
                      <Text className="text-[13px] self-center text-orange-500 min-h-[14px] tracking-tight">
                        Load More
                      </Text>
                    </Flex>
                  );
                }

                if (itemIndex < filterItems.length) {
                  const value = filterItems[itemIndex];
                  /**
                   * NOTE: Value can be null, so we just change its display to "(Blanks)"
                   * We only change the display, not the value that is passed to onSelectValue
                   * or the value being stored in the filterItems array.
                   * Thus we want normalizedCurrentValue to be null if value is null.
                   */
                  const normalizedCurrentValue =
                    value != null && isBool(value) ? value : normalizeValue(value);

                  const isSelected = selectedValues.includes(
                    normalizedCurrentValue
                  );

                  const valueToSelect = value ?? normalizedCurrentValue;
                  const uniqueKey = `${virtualItem.key}-${
                    valueToSelect ?? "(blanks)"
                  }`;

                  return (
                    <Flex
                      className="items-center border-b border-b-gray-100 p-2.5 cursor-pointer"
                      key={uniqueKey}
                      ref={rowVirtualizer.measureElement}
                      data-index={virtualItem.index}
                      onClick={() => onSelectValue(valueToSelect)}
                    >
                      <Text
                        className={clsx(
                          "flex-wrap break-all text-[13px] min-h-[14px] tracking-tight text-gray-900 leading-[120%] mr-8",
                          !isBool(value) && isEmpty(value) && "italic"
                        )}
                      >
                        {isBool(value) ? `${value}` : emptyCheck(value)}
                      </Text>
                      {isSelected && (
                        <Icon
                          as={MdsDoneRound}
                          size="sm"
                          color="gray.900"
                          className="absolute right-2"
                        />
                      )}
                    </Flex>
                  );
                }

                return null;
              })}
            </Box>
          </Box>
        )}
        <EmptyMessage />
      </Flex>
    </Flex>
  );
};
